import CIQClient, { HTTPClientResponse } from "./ciq-client";
import { BASE_URL_WITHOUT_GATEWAY } from "./endpoints";

type FeatureEntityLogParams = {
  projectToken: string;
  featureType: number;
  feature_instance_ids?: Array<string>;
  is_tracked?: boolean;
  void?: boolean;
  deleted_from_source?: boolean;
  include_comments?: boolean;
};

/** One in-flight full-log fetch per request shape (survives Strict Mode remount). */
const inFlightFeatureEntityLogFetches = new Map<
  string,
  Promise<HTTPClientResponse>
>();

function getFeatureEntityLogFetchKey({
  projectToken,
  featureType,
  is_tracked = true,
  void: voidOnly,
  deleted_from_source: deletedFromSource,
  include_comments: includeComments
}: FeatureEntityLogParams): string {
  return `${projectToken}|${featureType}|${is_tracked}|${voidOnly ?? ""}|${deletedFromSource ?? ""}|${includeComments ?? ""}`;
}

/** Clears in-flight fetch cache (tests only). */
export function clearFeatureEntityLogFetchDedupForTests(): void {
  inFlightFeatureEntityLogFetches.clear();
}

export const getFeatureEntityLog = ({
  projectToken,
  featureType,
  feature_instance_ids,
  is_tracked = true,
  void: voidOnly,
  deleted_from_source: deletedFromSource,
  include_comments: includeComments
}: FeatureEntityLogParams): Promise<HTTPClientResponse> => {
  const config = {
    headers: { Authorization: `Bearer ${projectToken}` }
  };

  let url = `${BASE_URL_WITHOUT_GATEWAY}/report-engine/logs/${featureType}?is_tracked=${is_tracked}`;

  if (voidOnly) {
    url += "&void=true";
  }

  if (deletedFromSource) {
    url += "&deleted_from_source=true";
  }

  if (includeComments) {
    url += "&include_comments=true";
  }

  if (feature_instance_ids && feature_instance_ids.length > 0) {
    url += `&feature_instance_ids=${feature_instance_ids.join(
      "&feature_instance_ids="
    )}`;
    return CIQClient.get({
      url,
      config
    });
  }

  const key = getFeatureEntityLogFetchKey({
    projectToken,
    featureType,
    is_tracked,
    void: voidOnly,
    deleted_from_source: deletedFromSource,
    include_comments: includeComments
  });
  let inFlight = inFlightFeatureEntityLogFetches.get(key);
  if (inFlight) {
    return inFlight;
  }

  inFlight = CIQClient.get({
    url,
    config
  }).finally(() => {
    if (inFlightFeatureEntityLogFetches.get(key) === inFlight) {
      inFlightFeatureEntityLogFetches.delete(key);
    }
  });
  inFlightFeatureEntityLogFetches.set(key, inFlight);
  return inFlight;
};
